import type { CanvasNote, Project, Task } from '@/src/renderer/lib/db/models'
import {
  CONTENT_TABLES, emptyTables, isDeviceSettingKey,
  type BackupRow, type BackupTable, type BackupTables, type ContentTable,
} from './format'

/**
 * **Backup wiederherstellen — der Plan** (ADR 0035): was geschrieben wird und was schon da ist.
 * Rein: Datei, Gerät und Konto rein, Zeilen raus. Geschrieben wird über die Datenquelle.
 *
 * - **Vorhandenes bleibt.** Eine Zeile, die auf dem Gerät lebt, wird nicht angefasst.
 * - **Fehlendes und Gelöschtes kommt zurück**, im Stand des Backups.
 * - **Abgleich** über die Kennung, dann über die Cloud-Kennung: Eine Zeile, die per Sync auf ein
 *   Gerät kam, trägt dort eine eigene `id` — gleich ist nur `cloudId`. Tags zusätzlich über den
 *   Namen, Gewohnheits-Einträge über Gewohnheit und Tag, Einstellungen über ihren Schlüssel.
 * - **Bezüge** (Projekt, Abschnitt, Elternaufgabe, Tags …) zeigen danach auf die Zeilen des Geräts.
 * - **Aus einem anderen Konto** kommt alles als Kopie mit neuen Kennungen.
 * - **Wiederhergestelltes folgt dem Gerät:** Liegt das Elternteil heute woanders, folgt die Zeile.
 */

export interface PlanRestoreInput {
  /** Die Zeilen der Datei, schon nach der Auswahl gefiltert. */
  file: BackupTables
  /** Alles auf dem Gerät, gelöschte Zeilen eingeschlossen. */
  device: BackupTables
  /** Die Datei stammt aus einem anderen Konto → Kopie mit neuen Kennungen. */
  foreignAccount: boolean
  /** Das angemeldete Konto (`userId` der geschriebenen Zeilen); `null` offline. */
  accountId: string | null
  /**
   * Cloud-Kennungen der Datei behalten — nur eigenes Konto mit Geräte-Sync. Eine Zeile, die die
   * Cloud als gelöscht kennt, kommt dann per Update zurück statt per Insert.
   */
  keepCloudIds: boolean
  /**
   * Eigene Cloud-Zeilen je Tabelle, nachgeschlagen über die `local_id` ({@link localIdsToLookUp}).
   * Eine Zeile, die beim Erstellen des Backups noch nicht hochgeladen war, kennt die Datei nur unter
   * ihrer lokalen Kennung; auf einem anderen Gerät liegt sie unter eigener `id`.
   */
  cloudRows?: Partial<Record<BackupTable, ReadonlyMap<string, CloudRowRef>>>
  newId?: () => string
}

/** Eine eigene Cloud-Zeile, gefunden über die `local_id` — gelöschte eingeschlossen. */
export interface CloudRowRef {
  cloudId: string
  deleted: boolean
}

/** Tabellen mit Weg in die Cloud — `settings` ist rein lokal. */
const CLOUD_TABLES: readonly BackupTable[] = [...CONTENT_TABLES, 'userPreferences', 'rewardUsages']

/**
 * Was in der Cloud nachzuschlagen ist (eigenes Konto mit Sync): Zeilen der Datei ohne Cloud-Kennung,
 * deren Kennung das Gerät nicht kennt. Je Tabelle die lokalen Kennungen.
 */
export function localIdsToLookUp(file: BackupTables, device: BackupTables): Partial<Record<BackupTable, string[]>> {
  const lookUp: Partial<Record<BackupTable, string[]>> = {}
  for (const table of CLOUD_TABLES) {
    const known = new Set((device[table] as AnyRow[]).map(row => row.id))
    const ids = (file[table] as AnyRow[]).filter(row => !row.cloudId && !known.has(row.id)).map(row => row.id)
    if (ids.length > 0) lookUp[table] = ids
  }
  return lookUp
}

export interface TableOutcome {
  /** Zurückgeholt: gab es auf dem Gerät gelöscht, oder die Cloud kennt sie. */
  revived: BackupRow[]
  /** Neu für dieses Konto bzw. Gerät. */
  added: BackupRow[]
  existing: number
  skipped: number
}

export interface RestorePlan {
  /** Die zu schreibenden Zeilen, fertig umgeschrieben. */
  write: BackupTables
  outcome: Record<ContentTable, TableOutcome>
  /** Ob sich eine Einstellung ändert — gleiche Werte zählen nicht. */
  overwritesSettings: boolean
}

type AnyRow = BackupRow & { id: string; cloudId?: string | null; deletedAt?: Date | null; userId?: string | null }
type Status = 'existing' | 'revived' | 'added'
interface Match { status: Status; targetId: string; deviceRow?: AnyRow; cloudId?: string }

/** Eltern vor Kindern; sonst fände ein Kind sein Elternteil noch nicht geschrieben vor. */
function parentsFirst<T extends { id: string }>(rows: readonly T[], parentOf: (row: T) => string | null | undefined): T[] {
  const byId = new Map(rows.map(row => [row.id, row]))
  const depth = new Map<string, number>()
  const depthOf = (row: T, seen = new Set<string>()): number => {
    const known = depth.get(row.id)
    if (known !== undefined) return known
    const parentId = parentOf(row)
    const parent = parentId ? byId.get(parentId) : undefined
    const d = parent && !seen.has(parent.id) ? depthOf(parent, seen.add(row.id)) + 1 : 0
    depth.set(row.id, d)
    return d
  }
  return [...rows].sort((a, b) => depthOf(a) - depthOf(b))
}

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

export function planRestore(input: PlanRestoreInput): RestorePlan {
  const { device, foreignAccount, accountId, keepCloudIds, cloudRows } = input
  const newId = input.newId ?? (() => crypto.randomUUID())
  // Gelöschtes gehört nicht ins Backup; kommt es doch vor, zählt es nicht. Ein laufender Fokus-Abschnitt auch nicht.
  const file = Object.fromEntries(Object.entries(input.file).map(([table, rows]) => [
    table,
    (rows as AnyRow[]).filter(row => !row.deletedAt && !(table === 'timeEntries' && (row as { endTime?: Date | null }).endTime == null)),
  ])) as unknown as BackupTables

  const write = emptyTables()
  const outcome = Object.fromEntries(CONTENT_TABLES.map(t => [t, { revived: [], added: [], existing: 0, skipped: 0 }])) as unknown as Record<ContentTable, TableOutcome>

  /** Datei-Kennung → Ziel auf dem Gerät, je Tabelle. */
  const matches = new Map<BackupTable, Map<string, Match>>()
  /** Was nach dem Wiederherstellen lebt: das Lebende des Geräts plus das Geschriebene. */
  const alive = new Map<BackupTable, Map<string, AnyRow>>()
  for (const table of Object.keys(device) as BackupTable[]) {
    alive.set(table, new Map((device[table] as AnyRow[]).filter(row => !row.deletedAt).map(row => [row.id, row])))
  }

  const deviceIndex = (table: BackupTable) => {
    const rows = device[table] as AnyRow[]
    return {
      byId: new Map(rows.map(row => [row.id, row])),
      byCloudId: new Map(rows.filter(row => row.cloudId).map(row => [row.cloudId as string, row])),
    }
  }

  /** Eine Zeile des Geräts, die dieselbe ist — Lebende vor Gelöschten bei natürlichen Schlüsseln. */
  const naturalMatch = (table: BackupTable, row: AnyRow): AnyRow | undefined => {
    const pick = (candidates: AnyRow[]) => candidates.find(c => !c.deletedAt) ?? candidates[0]
    switch (table) {
      case 'tags': {
        const name = (row as { name: string }).name.trim().toLowerCase()
        return pick((device.tags as AnyRow[]).filter(t => (t as { name: string }).name.trim().toLowerCase() === name))
      }
      case 'settings':
        return pick((device.settings as AnyRow[]).filter(s => (s as { key: string }).key === (row as { key: string }).key))
      case 'userPreferences':
        return pick((device.userPreferences as AnyRow[]).filter(p => (p as { key: string }).key === (row as { key: string }).key))
      case 'rewardUsages':
        return pick((device.rewardUsages as AnyRow[]).filter(u => (u as { rewardId: string }).rewardId === (row as { rewardId: string }).rewardId))
      case 'habitLogs': {
        const habitId = matches.get('habits')?.get((row as { habitId: string }).habitId)?.targetId ?? (row as { habitId: string }).habitId
        const date = (row as { date: string }).date
        return pick((device.habitLogs as AnyRow[]).filter(l => (l as { habitId: string }).habitId === habitId && (l as { date: string }).date === date))
      }
      default:
        return undefined
    }
  }

  /** Schritt 1: Ziel und Stand je Datei-Zeile. Bei Tags fangen gleiche Namen in der Datei einander auf. */
  const assign = (table: BackupTable) => {
    const { byId, byCloudId } = deviceIndex(table)
    const result = new Map<string, Match>()
    const addedTagByName = new Map<string, string>()
    for (const row of file[table] as AnyRow[]) {
      let deviceRow = !foreignAccount ? byId.get(row.id) ?? (row.cloudId ? byCloudId.get(row.cloudId) : undefined) : undefined
      // Nur unter der lokalen Kennung bekannt: Die Cloud sagt, unter welcher Cloud-Kennung sie liegt.
      const cloud = !foreignAccount && !deviceRow && !row.cloudId ? cloudRows?.[table]?.get(row.id) : undefined
      if (cloud) deviceRow = byCloudId.get(cloud.cloudId)
      if (cloud && !deviceRow) {
        // Lebt sie in der Cloud, ist ihr Stand neuer als der des Backups; gelöscht kommt sie mit ihrer Kennung zurück.
        result.set(row.id, cloud.deleted ? { status: 'revived', targetId: row.id, cloudId: cloud.cloudId } : { status: 'existing', targetId: row.id })
        continue
      }
      // Ein gelöschter Tag tritt hinter einen lebenden gleichen Namens zurück — sonst gäbe es ihn zweimal.
      const natural = naturalMatch(table, row)
      if (!deviceRow || (table === 'tags' && deviceRow.deletedAt && natural && !natural.deletedAt)) deviceRow = natural
      if (deviceRow) {
        result.set(row.id, { status: deviceRow.deletedAt ? 'revived' : 'existing', targetId: deviceRow.id, deviceRow })
        continue
      }
      const tagName = table === 'tags' ? (row as { name: string }).name.trim().toLowerCase() : null
      const twin = tagName !== null ? addedTagByName.get(tagName) : undefined
      if (twin) {
        result.set(row.id, { status: 'existing', targetId: twin })
        continue
      }
      // Einstellungen und Belohnungs-Nutzung tragen ihren Schlüssel als Kennung, auf jedem Gerät gleich.
      const targetId = table === 'userPreferences' ? (row as { key: string }).key
        : table === 'rewardUsages' ? (row as { rewardId: string }).rewardId
          : foreignAccount ? newId() : row.id
      if (tagName !== null) addedTagByName.set(tagName, targetId)
      // Die Cloud kennt die Zeile (gelöscht und hier schon weggeräumt): Sie kommt zurück, nicht neu.
      const knownToCloud = !foreignAccount && keepCloudIds && !!row.cloudId
      result.set(row.id, { status: knownToCloud ? 'revived' : 'added', targetId })
    }
    matches.set(table, result)
  }

  /** Ein Bezug aus der Datei → die Kennung, die nach dem Wiederherstellen lebt; `null`, wenn keine. */
  const resolve = (table: BackupTable, fileRefId: string | null | undefined): string | null => {
    if (!fileRefId) return null
    const match = matches.get(table)?.get(fileRefId)
    const living = alive.get(table)!
    if (match) return living.has(match.targetId) ? match.targetId : null
    // Nicht gewählt, aber auf dem Gerät: Der Bezug stimmt schon.
    return !foreignAccount && living.has(fileRefId) ? fileRefId : null
  }
  const final = <T>(table: BackupTable, id: string) => alive.get(table)!.get(id) as T | undefined

  /** Schritt 2: schreiben, was fehlt oder gelöscht ist — `remap` gibt `null`, wenn ein nötiger Bezug fehlt. */
  const restore = <T extends AnyRow>(table: BackupTable, rows: readonly T[], remap: (row: T) => Partial<T> | null) => {
    for (const row of rows) {
      const match = matches.get(table)!.get(row.id)!
      const counted = (CONTENT_TABLES as readonly string[]).includes(table) ? outcome[table as ContentTable] : null
      if (match.status === 'existing') {
        if (counted) counted.existing++
        continue
      }
      const changes = remap(row)
      if (!changes) {
        if (counted) counted.skipped++
        continue
      }
      const written = {
        ...row,
        ...changes,
        id: match.targetId,
        cloudId: match.deviceRow?.cloudId ?? match.cloudId ?? (keepCloudIds && !foreignAccount ? row.cloudId ?? null : null),
        userId: accountId,
        deletedAt: null,
        synced: false,
        syncedAt: null,
        // Die Kopie trägt keine Cloud-Kennung des fremden Kontos weiter (umgewandelte Unteraufgabe, T14 #179).
        ...(foreignAccount && 'adoptCloudId' in row ? { adoptCloudId: null } : {}),
      } as T
      ;(write[table] as AnyRow[]).push(written)
      alive.get(table)!.set(written.id, written)
      if (counted) (match.status === 'revived' ? counted.revived : counted.added).push(written)
    }
  }

  for (const table of ['tags', 'projects', 'sections', 'tasks', 'canvasNotes', 'goals', 'habits'] as const) assign(table)

  restore('tags', file.tags as AnyRow[], () => ({}))
  restore('projects', parentsFirst(file.projects, p => p.parentId) as (Project & AnyRow)[], project => {
    // Ein Unterprojekt allein kommt ohne Elternteil und steht oben im Lebensbereich.
    const parentId = resolve('projects', project.parentId)
    const parent = parentId ? final<Project>('projects', parentId) : undefined
    return { parentId, ...(parent ? { lifeSpace: parent.lifeSpace } : {}) }
  })
  restore('sections', file.sections as AnyRow[], section => {
    const projectId = resolve('projects', (section as { projectId: string }).projectId)
    return projectId ? { projectId } : null
  })
  restore('tasks', parentsFirst(file.tasks, t => t.parentTaskId) as (Task & AnyRow)[], task => {
    const tags = task.tags.map(tag => resolve('tags', tag)).filter((id): id is string => !!id)
    if (task.parentTaskId) {
      const parentId = resolve('tasks', task.parentTaskId)
      const parent = parentId ? final<Task>('tasks', parentId) : undefined
      if (!parent) return null
      return { parentTaskId: parent.id, projectId: parent.projectId, sectionId: parent.sectionId, lifeSpace: parent.lifeSpace, tags }
    }
    if (!task.projectId) return { sectionId: null, tags }
    const projectId = resolve('projects', task.projectId)
    const project = projectId ? final<Project>('projects', projectId) : undefined
    if (!project) return null
    const sectionId = resolve('sections', task.sectionId)
    const section = sectionId ? final<{ projectId: string }>('sections', sectionId) : undefined
    return { projectId: project.id, sectionId: section?.projectId === project.id ? sectionId : null, lifeSpace: project.lifeSpace, tags }
  })
  // Bereiche vor den Zetteln, die zu ihnen gehören.
  restore('canvasNotes', [...file.canvasNotes].sort((a, b) => Number(b.kind === 'area') - Number(a.kind === 'area')) as (CanvasNote & AnyRow)[], note => {
    const projectId = resolve('projects', note.projectId)
    if (!projectId) return null
    return { projectId, areaId: resolve('canvasNotes', note.areaId), taskId: resolve('tasks', note.taskId) }
  })
  for (const table of ['taskComments', 'taskReminders', 'timeEntries'] as const) {
    assign(table)
    restore(table, file[table] as AnyRow[], row => {
      const taskId = resolve('tasks', (row as { taskId: string }).taskId)
      return taskId ? { taskId } : null
    })
  }
  assign('projectComments')
  restore('projectComments', file.projectComments as AnyRow[], row => {
    const projectId = resolve('projects', (row as { projectId: string }).projectId)
    return projectId ? { projectId } : null
  })
  restore('goals', file.goals as AnyRow[], () => ({}))
  restore('habits', file.habits as AnyRow[], () => ({}))
  assign('habitLogs')
  restore('habitLogs', file.habitLogs as AnyRow[], row => {
    const habitId = resolve('habits', (row as { habitId: string }).habitId)
    return habitId ? { habitId } : null
  })

  const overwritesSettings = planSettings(file, write, matches, assign, { accountId, keepCloudIds, foreignAccount })
  return { write, outcome, overwritesSettings }
}

/**
 * **Einstellungen: Das Backup gewinnt** (Entscheidung 2a). Jede Einstellung im Backup bekommt
 * dessen Wert; was das Backup nicht kennt, bleibt. Gleiche Werte werden nicht geschrieben.
 */
function planSettings(
  file: BackupTables,
  write: BackupTables,
  matches: Map<BackupTable, Map<string, Match>>,
  assign: (table: BackupTable) => void,
  { accountId, keepCloudIds, foreignAccount }: { accountId: string | null; keepCloudIds: boolean; foreignAccount: boolean },
): boolean {
  let any = false
  const valueOf: Record<'settings' | 'userPreferences' | 'rewardUsages', (row: any) => unknown> = {
    settings: row => row.value,
    userPreferences: row => row.value,
    rewardUsages: row => row.usedAt?.getTime?.() ?? null,
  }
  for (const table of ['settings', 'userPreferences', 'rewardUsages'] as const) {
    const rows = (file[table] as AnyRow[]).filter(row => table !== 'settings' || !isDeviceSettingKey((row as { key: string }).key))
    assign(table)
    for (const row of rows) {
      const match = matches.get(table)!.get(row.id)!
      const deviceRow = match.deviceRow
      if (deviceRow && !deviceRow.deletedAt && sameValue(valueOf[table](deviceRow), valueOf[table](row))) continue
      any = true
      ;(write[table] as AnyRow[]).push({
        ...row,
        id: match.targetId,
        ...(table === 'settings' ? {} : {
          cloudId: deviceRow?.cloudId ?? (keepCloudIds && !foreignAccount ? row.cloudId ?? null : null),
          userId: accountId,
          deletedAt: null,
        }),
        synced: false,
        syncedAt: null,
      } as AnyRow)
    }
  }
  return any
}
